import type { JSX, ReactNode } from 'react';
import React from 'react';

import { NavItem, NavMenu } from '@feathr/components';

// Minimal config format: [path, desc, keywords, label?, children?]
type NavConfig = [string, string, string, string?, ReactNode?];
type MenuConfig = NavConfig & { menu: ReactNode; name?: string; hasDefaultChild?: boolean };

interface INavOptions {
  localUrl: (path: string) => string;
  t: (key: string) => string;
  conditions?: Record<number, boolean>;
  tooltips?: Record<number, string>;
}

export function createNavItems(configs: NavConfig[], options: INavOptions): JSX.Element[] {
  const { localUrl, conditions = {}, tooltips = {} } = options;

  return configs
    .map((config, index) => {
      const [path, desc, keywords, label, children] = config;

      // Skip if condition exists and is false
      if (index in conditions && !conditions[index]) {
        return null;
      }

      const finalLabel = label || desc;

      return (
        <NavItem
          data-command={'true'}
          data-command-description={desc}
          data-command-keywords={keywords}
          data-command-label={finalLabel}
          key={path}
          to={localUrl(path)}
          tooltip={tooltips[index]}
        >
          {children || (label ?? desc)}
        </NavItem>
      );
    })
    .filter(Boolean) as JSX.Element[];
}

export function createNavMenus(configs: MenuConfig[], options: INavOptions): JSX.Element[] {
  const { localUrl, t, conditions = {}, tooltips = {} } = options;

  return configs
    .map((config, index) => {
      const [path, desc, keywords, label, children] = config;

      // Skip if condition exists and is false
      if (index in conditions && !conditions[index]) {
        return null;
      }

      const finalLabel = label || desc;

      return (
        <NavMenu
          data-command={'true'}
          data-command-description={desc}
          data-command-keywords={keywords}
          data-command-label={finalLabel}
          hasDefaultChild={config.hasDefaultChild}
          key={path}
          menu={config.menu}
          name={config.name}
          to={localUrl(path)}
          tooltip={tooltips[index]}
        >
          {children || t(label ? label : desc)}
        </NavMenu>
      );
    })
    .filter(Boolean) as JSX.Element[];
}

// Ultra-compact helper for mixed nav items and menus
export function nav(
  path: string,
  desc: string,
  keywords: string,
  label?: string,
  childrenOrMenu?: ReactNode | { menu: ReactNode; name?: string },
): NavConfig | MenuConfig {
  if (childrenOrMenu && typeof childrenOrMenu === 'object' && 'menu' in childrenOrMenu) {
    return Object.assign([path, desc, keywords, label] as NavConfig, childrenOrMenu);
  }
  return [path, desc, keywords, label, childrenOrMenu as ReactNode];
}
